import React, { useState, useEffect } from 'react';
import { getApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { formatApiDate } from '../../../Utils/subscriptionTrial';

/** Amounts come back in cents ("amount": 50 -> $0.50). */
const formatCents = (cents, currency = 'usd') => {
  const value = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

/** "2026-08" -> "August 2026" */
const formatMonthLabel = (month) => {
  if (!month) return '';
  const [year, monthNumber] = String(month).split('-');
  const date = new Date(Number(year), Number(monthNumber) - 1, 1);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

const BillingUsage = () => {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [month, setMonth] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    fetchCallUsage(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const fetchCallUsage = async (selectedMonth) => {
    setLoading(true);
    try {
      const response = await getApi('/billing/call-usage', {
        params: selectedMonth ? { month: selectedMonth } : {},
      });

      if (response?.statusCode === 200) {
        const data = response?.data || {};
        setUsage(data);
        // Open every month by default so the charges are visible without a click.
        const opened = {};
        (data.months || []).forEach((entry) => {
          opened[entry.month] = true;
        });
        setExpandedMonths(opened);
      } else {
        setUsage(null);
        toast.error(response?.message || 'Failed to load call usage');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load call usage';
      setUsage(null);
      toast.error(errorMessage);
      console.error('Error fetching call usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  const months = usage?.months || [];
  const totalCalls = usage?.total_calls || 0;
  const totalAmountCents = usage?.total_amount_cents || 0;
  // The totals are currency-less, so take the currency from the first charge seen.
  const currency = months?.[0]?.charges?.[0]?.currency || 'usd';

  return (
    <div className="billing-usage-page">
      <div className="billing-usage-container">
        <div className="billing-usage-card">
          <div className="billing-usage-header">
            <div>
              <h2 className="billing-usage-title">Twilio Call Usage</h2>
              <p className="billing-usage-subtitle">
                Tenant-screening call charges billed to your account
              </p>
            </div>
            <div className="billing-usage-filter">
              <label htmlFor="billing-usage-month" className="billing-usage-filter-label">
                Filter by month
              </label>
              <div className="billing-usage-filter-controls">
                <input
                  id="billing-usage-month"
                  type="month"
                  className="billing-usage-month-input"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />
                {month && (
                  <button
                    type="button"
                    className="billing-usage-clear-button"
                    onClick={() => setMonth('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="billing-usage-empty">Loading call usage...</div>
          ) : (
            <>
              <div className="billing-usage-summary">
                <div className="billing-usage-stat">
                  <span className="billing-usage-stat-label">Total calls</span>
                  <span className="billing-usage-stat-value">{totalCalls}</span>
                </div>
                <div className="billing-usage-stat">
                  <span className="billing-usage-stat-label">Total charged</span>
                  <span className="billing-usage-stat-value">
                    {formatCents(totalAmountCents, currency)}
                  </span>
                </div>
                <div className="billing-usage-stat">
                  <span className="billing-usage-stat-label">
                    {month ? 'Selected month' : 'Months billed'}
                  </span>
                  <span className="billing-usage-stat-value">
                    {month ? formatMonthLabel(month) : months.length}
                  </span>
                </div>
              </div>

              {months.length === 0 ? (
                <div className="billing-usage-empty">
                  {month
                    ? `No call charges found for ${formatMonthLabel(month)}.`
                    : 'No call charges yet.'}
                </div>
              ) : (
                <div className="billing-usage-months">
                  {months.map((entry) => {
                    const charges = entry.charges || [];
                    const isExpanded = !!expandedMonths[entry.month];

                    return (
                      <div className="billing-usage-month-block" key={entry.month}>
                        <button
                          type="button"
                          className="billing-usage-month-header"
                          onClick={() => toggleMonth(entry.month)}
                          aria-expanded={isExpanded}
                        >
                          <span className="billing-usage-month-name">
                            {formatMonthLabel(entry.month)}
                          </span>
                          <span className="billing-usage-month-meta">
                            <span className="billing-usage-month-calls">
                              {entry.total_calls} {entry.total_calls === 1 ? 'call' : 'calls'}
                            </span>
                            <span className="billing-usage-month-amount">
                              {formatCents(
                                entry.total_amount_cents,
                                charges?.[0]?.currency || currency
                              )}
                            </span>
                            <span
                              className={`billing-usage-chevron${
                                isExpanded ? ' billing-usage-chevron-open' : ''
                              }`}
                              aria-hidden="true"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="billing-usage-table-wrapper">
                            {charges.length === 0 ? (
                              <div className="billing-usage-empty">
                                No charges for this month.
                              </div>
                            ) : (
                              <table className="billing-usage-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Charge ID</th>
                                    <th>Status</th>
                                    <th className="billing-usage-amount-col">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {charges.map((charge) => (
                                    <tr key={charge.id}>
                                      <td>{formatApiDate(charge.createdAt)}</td>
                                      <td className="billing-usage-charge-id" title={charge.id}>
                                        {charge.id}
                                      </td>
                                      <td>
                                        <span
                                          className={`billing-usage-status billing-usage-status-${String(
                                            charge.status || 'unknown'
                                          ).toLowerCase()}`}
                                        >
                                          {charge.status || 'unknown'}
                                        </span>
                                      </td>
                                      <td className="billing-usage-amount-col">
                                        {formatCents(charge.amount, charge.currency || currency)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingUsage;
